import { polymorphicComponentWrapper } from "../../../../utilities";
import { Avatar } from "../../../avatar";
import { Group } from "../../../group";
import { Icon } from "../../../icon";
import { Stack } from "../../../stack";
import { Card } from "../../card-component";
import { CustomCardIdFactory, ICardIdSkeletonProps, IProps, IPropsCardIdDetails } from "./card-id.types";
import { faUserVneck } from "@fortawesome/pro-solid-svg-icons";
import { Text } from "../../../text";
import styles from "./card-id.module.scss";
import { kebabCase } from "lodash";

const CardId = polymorphicComponentWrapper<CustomCardIdFactory, IProps>(
    "CardId",
    ({ label, value, icon, children, ...otherProps }, ref) => {
        return (
            <Card
                ref={ref}
                classNames={{ root: styles["root"], section: styles["section"] }}
                {...otherProps}
                shadow="xs"
                bd="1px solid var(--border-default-color)"
                data-testid={`card-id-${kebabCase(label)}`}
            >
                <Card.Section p="md" bdrs="md" className={styles["heading-section"]}>
                    <Group gap="sm" align="center">
                        <Avatar variant="filled" size="md" bg="var(--surface-gray-transparent)">
                            <Icon icon={icon} color="var(--mantine-color-neutral-9)" />
                        </Avatar>
                        <Stack gap="xs">
                            <Text dimmed size="xxs" data-testid={`card-id-${kebabCase(label)}-label`}>
                                {label}
                            </Text>
                            {value && (
                                <Text variant="semibold" size="lg" data-testid={`card-id-${kebabCase(label)}-value`}>
                                    {value}
                                </Text>
                            )}
                        </Stack>
                    </Group>
                </Card.Section>
                <Card.Section p="md">{children}</Card.Section>
            </Card>
        );
    }
);

const CardIdDetails = ({ icon, value, "data-testid": dataTestId }: IPropsCardIdDetails) => {
    return (
        <Group gap="sm" data-testid={dataTestId ?? `card-id-details-${kebabCase(value ?? "")}`}>
            <Avatar variant="filled" size="sm" bg="var(--surface-gray-transparent)">
                <Icon icon={icon} color="var(--mantine-color-neutral-9)" />
            </Avatar>
            <Text size="sm">{value}</Text>
        </Group>
    );
};

const CardIdSkeleton = ({ label }: ICardIdSkeletonProps) => {
    const skeletonColor = "var(--surface-gray-transparent-color)";
    return (
        <Card
            shadow="xs"
            bd="1px solid var(--border-default-color)"
            classNames={{ root: styles["root"], section: styles["section"] }}
        >
            <Card.Section p="md" bdrs="md" className={styles["heading-section"]}>
                <Group gap="sm">
                    <Avatar variant="filled" size="md" bg="var(--surface-gray-transparent)">
                        <Icon icon={faUserVneck} color="var(--mantine-color-neutral-9)" />
                    </Avatar>
                    <Stack gap="xs">
                        <Text dimmed size="xxs">
                            {label}
                        </Text>
                        <Text.Skeleton color={skeletonColor} disableDarken size="lg" width={120} />
                    </Stack>
                </Group>
            </Card.Section>
            <Card.Section p="md">
                <Stack gap="sm">
                    <Group gap="xs">
                        <Avatar.Skeleton size="sm" />
                        <Text.Skeleton color={skeletonColor} disableDarken size="sm" width={120} />
                    </Group>
                    <Group gap="xs">
                        <Avatar.Skeleton size="sm" />

                        <Text.Skeleton color={skeletonColor} disableDarken size="sm" width={120} />
                    </Group>
                    <Group gap="xs">
                        <Avatar.Skeleton size="sm" />
                        <Text.Skeleton color={skeletonColor} disableDarken size="sm" width={120} />
                    </Group>
                </Stack>
            </Card.Section>
        </Card>
    );
};

CardId.Details = CardIdDetails;
CardId.Skeleton = CardIdSkeleton;

export { CardId, CardIdSkeleton, CardIdDetails };
